/**
 * The 360 viewer, loaded only when a 360 is opened (photo-sphere-viewer and
 * its CSS are a chunk of their own), with a loading line meanwhile and a plain
 * fallback — the picture itself — should the chunk or WebGL fail.
 */

import React, { Suspense } from 'react';
import type { OpeningMarker } from './panoMarkers';

export interface PanoLabels {
  kind: Record<OpeningMarker['kind'], string>;
  dims: (width: number, height: number) => string;
  loading: string;
  failed: string;
  openPicture: string;
}

export interface PanoViewerProps {
  src: string;
  markers: OpeningMarker[];
  labels: PanoLabels;
  onReady?: () => void;
  onError?: (e: unknown) => void;
}

const PanoViewerImpl = React.lazy(() => import('./PanoViewerImpl'));

class PanoErrorBoundary extends React.Component<{ fallback: React.ReactNode; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: unknown) { if (process.env.NODE_ENV !== 'production') console.error('360 viewer failed', error); }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

const Failed: React.FC<{ src: string; labels: PanoLabels }> = ({ src, labels }) => (
  <div className="rpano-fallback">
    <p>{labels.failed}</p>
    <a href={src} target="_blank" rel="noopener noreferrer">{labels.openPicture}</a>
  </div>
);

export const PanoViewer: React.FC<PanoViewerProps> = (props) => {
  const [failed, setFailed] = React.useState(false);
  if (failed) return <Failed src={props.src} labels={props.labels} />;
  return (
    <PanoErrorBoundary fallback={<Failed src={props.src} labels={props.labels} />}>
      <Suspense fallback={<div className="rpano-loading">{props.labels.loading}</div>}>
        <PanoViewerImpl {...props} onError={(e) => { props.onError?.(e); setFailed(true); }} />
      </Suspense>
    </PanoErrorBoundary>
  );
};
